/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import React, {useContext, useEffect, useState} from 'react';
import Header from '../Header/Header';
import './ValidateIdentityInternal.scss';
import {ExdsButton, ExdsGrid, ExdsLayout, ExdsSpacing, ExdsText} from "@exds/core";
import {ExdsRadio, ExdsRadioGroup, ExdsTextInput} from "@exds/controls";
import {ExdsIconDevicePhone, ExdsIconPhoneFilled} from "@exds/icons";
import intlTelInput from 'intl-tel-input/intlTelInputWithUtils';
import 'intl-tel-input/build/css/intlTelInput.css';
import {TuxLoadingIndicator, TuxTextInput} from "@tuxedo/react-component-library";
import StartOverModal from "../StartOverModal/StartOverModal";
import AppContext from "../../AppContext";
import {ExdsAlert} from "@exds/feedback";
import Error from "../Error/Error";
import AccessCodeInternal from "./AccessCodeInternal/AccessCodeInternal";
import {
  ERROR_ALL_FIELDS_REQUIRED, NOT_ALLOWED_TO_ONBOARD_TODAY, ONBOARDED_USER,
  RADIO_BUTTONS_ERROR_TEXT,
  VALIDATE_IDENTITY_ERROR_CONTENT
} from "../../constants/constants";
import {apiPost} from "../../service/service";
import {doEncrypt, generateKeyPair} from "../E2ee/E2ee";
import {addPageLoadEvent} from "../AdobeAnalytics/AdobeAnalytics";

export default function ValidateIdentityInternal() {
  const pageStates = {
    VALIDATE_IDENTITY: 'VALIDATE_IDENTITY',
    LOADING: 'LOADING',
    ACCESS_CODE: 'ACCESS_CODE',
    VALIDATE_IDENTITY_ERROR: 'VALIDATE_IDENTITY_ERROR',
    CREATE_PASSWORD: 'CREATE_PASSWORD'
  };
  const {
    sidContext,
    phoneNumberContext,
    deliveryMethodContext,
    firstNameContext,
    lastNameContext,
    transactionIdContext,
    hashContext,
    expirationTimeContext,
    plainTextPhoneNumberContext,
    isSoftTokenEnabledContext,
    isSmartLogonEnabledContext,
    mppUrlContext,
    loadAA
  } = useContext(AppContext);
  const sid = sidContext.at(0);
  const setSid = sidContext.at(1);
  const deliveryMethod = deliveryMethodContext.at(0);
  const setDeliveryMethod = deliveryMethodContext.at(1);
  const plainTextPhoneNumber = plainTextPhoneNumberContext.at(0);
  const setPlainTextPhoneNumber = plainTextPhoneNumberContext.at(1);
  const setFirstName = firstNameContext.at(1);
  const setLastName = lastNameContext.at(1);
  const setTransactionId = transactionIdContext.at(1);
  const setHash = hashContext.at(1);
  const setExpirationTime = expirationTimeContext.at(1);
  const setIsSoftTokenEnabled = isSoftTokenEnabledContext.at(1);
  const setIsSmartLogonEnabled = isSmartLogonEnabledContext.at(1);
  const setPhoneNumber = phoneNumberContext.at(1);
  const mppUrl = mppUrlContext.at(0);
  const [isStartOverModalOpen, setIsStartOverModalOpen] = useState(false);
  const [error, setError] = useState({correlationId: '', errorMessage: '', buttonText: '', buttonLink: ''});
  const [hasSidError, setHasSidError] = useState(false);
  const [hasMobileNumberError, setHasMobileNumberError] = useState(false);
  const [hasDeliveryMethodError, setHasDeliveryMethodError] = useState(false);
  const [countryCode, setCountryCode] = useState(null);
  const [dialCode, setDialCode] = useState('+1');
  const [keyPair, setKeyPair] = useState({});
  const [pageState, setPageState] = useState(pageStates.VALIDATE_IDENTITY);
  const [isVisible, setIsVisible] = useState(false);
  const SID_INPUT_MICROCOPY_TEXT = "Enter your Standard ID (SID) that was sent to you upon hire";
  const MOBILE_INPUT_MICROCOPY_TEXT = "Enter your mobile phone number that we have on file starting with country code";
  const ERROR_MOBILE_INPUT_INVALID = "Please provide a valid phone number starting with the country code";
  const [pageName, setPageName] = useState('Validate Identity');

  useEffect(() => {
    if(loadAA) {
      addPageLoadEvent(pageName);
    }
  }, [loadAA]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const setKeys = async () => {
      const pair = await generateKeyPair();
      setKeyPair(pair);
    };
    setKeys();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => clearTimeout(timer);
  });

  function setPageReloadState(){
    setPageState(pageStates.VALIDATE_IDENTITY);
  }

  function handleError(response) {
    const error = response.data.errorCode ;
    const errorDetails = getErrorDetails(error, response.correlationId);
    setError({...error, ...errorDetails});
  }

  function getErrorDetails(error, correlationId) {
    switch (error) {
      case "NOT_TODAY":
        return {
          correlationId,
          errorMessage: NOT_ALLOWED_TO_ONBOARD_TODAY
        };
      case "ACTIVE":
        return {
          correlationId,
          errorMessage: ONBOARDED_USER,
          buttonText: 'Update my password',
          buttonLink: mppUrl,
          externalRedirectLink: true,
          windowOpenTarget: '_blank',
          windowDimensions: 'width=800,height=600'
        };
      default:
        return {
          correlationId,
          errorMessage: VALIDATE_IDENTITY_ERROR_CONTENT,
          buttonText: 'Try Again',
          buttonLink: '/validateIdentity',
          windowOpenTarget: '_self',
        };
    }
  }

  useEffect(() => {
    if (pageState === pageStates.VALIDATE_IDENTITY) {
      const mobileInputElement = document.getElementById('mobile-input');
      const iti = intlTelInput(mobileInputElement, {
        initialCountry: countryCode || 'us',
        separateDialCode: true,
        strictMode: true,
        validationNumberType: 'MOBILE',
        autoPlaceholder: 'off'
      });

      const handleMobileInputChange = () => {
        const valid = iti.isValidNumber();
        setHasMobileNumberError(!valid);
        if (valid) mobileInputElement.value = (iti.getNumber(intlTelInput));
        const countryData = iti.getSelectedCountryData();
        setCountryCode(countryData.iso2);
        const dialCode = `+${countryData.dialCode}`;
        setDialCode(dialCode);
        setPlainTextPhoneNumber(mobileInputElement.value.replace(dialCode, ""));
      };

      mobileInputElement.addEventListener('change', handleMobileInputChange);

      return () => {
        mobileInputElement.removeEventListener('change', handleMobileInputChange);
        iti.destroy();
      };
    }
  }, [pageState]);// eslint-disable-line react-hooks/exhaustive-deps

  const  handleRadioChange = (selectedRadio) => {
    if(selectedRadio.id.includes('sms')) {
      setDeliveryMethod('SMS');
    } else {
      setDeliveryMethod('VOICE');
    }
    setHasDeliveryMethodError(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validateForm()) {
      setPageState(pageStates.LOADING);
      apiPost('/api/validateUser', {
        sid,
        phoneNumber: await doEncrypt(dialCode + plainTextPhoneNumber),
        deliveryMethod,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }, {'PUBLIC_KEY': keyPair.publicKey})
        .then(async (response) => {
          if (response.status === 200) {
            if(!response.data.errorCode) {
              setFirstName(response.data.firstName);
              setLastName(response.data.lastName);
              setTransactionId(response.data.transactionId);
              setHash(response.data.hash);
              setExpirationTime(response.data.expirationTime);
              setIsSoftTokenEnabled(response.data.softTokenEnabled);
              setIsSmartLogonEnabled(response.data.smartLogonEnabled);
              setPageState(pageStates.ACCESS_CODE);
              setPhoneNumber(response.data.phoneNumber);
            } else {
              handleError(response);
              setPageState(pageStates.VALIDATE_IDENTITY_ERROR);
            }
          } else {
            handleError(response);
            setPageState(pageStates.VALIDATE_IDENTITY_ERROR);
          }
        })
        .catch((err) => {
          setError({...error, errorMessage: VALIDATE_IDENTITY_ERROR_CONTENT, correlationId: err?.correlationId,
            buttonText: 'Try Again', buttonLink: '/validateIdentity'});
          setPageState(pageStates.VALIDATE_IDENTITY_ERROR);
        });
    }

  };

  const validateForm = () => {
    let valid = true;
    if (!validateSid()) valid = false;
    if (!validatePhoneNumber()) valid = false;
    if (!validateDeliveryMethod()) valid = false;
    return valid;
  };

  const validateSid = () => {
    const valid = Boolean(sid);
    setHasSidError(!valid);
    return valid;
  };

  const validatePhoneNumber = () => {
    const valid = Boolean(plainTextPhoneNumber);
    setHasMobileNumberError(!valid);
    return valid;
  };

  const validateDeliveryMethod = () => {
    const valid = Boolean(deliveryMethod);
    setHasDeliveryMethodError(!valid);
    return valid;
  };

  return (
    <>
      <Header pageName={pageName} sid={pageState === pageStates.ACCESS_CODE ? sid : ''}/>
      {pageState === pageStates.LOADING && <div className="loading-spinner-validate-identity"><TuxLoadingIndicator/></div>}
      {pageState === pageStates.ACCESS_CODE && !error.errorMessage &&
        <AccessCodeInternal
          setPageReloadState={setPageReloadState}
          dialCode={dialCode}
          setPageName={setPageName}
          pageState={pageState}
          setPageState={setPageState}
          pageStates={pageStates}
          setError={setError}
        />}
      {pageState === pageStates.VALIDATE_IDENTITY_ERROR && error.errorMessage &&
        <Error
          content={error.errorMessage}
          buttonTitle={error.buttonText}
          buttonLink={error.buttonLink}
          correlationId={error.correlationId}
          externalRedirectLink={error.externalRedirectLink}
          pageName={pageName}
          sid={sid}
          windowOpenTarget={error.windowOpenTarget}
          windowDimensions={error.windowDimensions ? error.windowDimensions : null}
        />
      }
      {pageState === pageStates.VALIDATE_IDENTITY && <ExdsGrid
        alignItems="center"
        columnSpan={12}
        container
        flexDirection="row"
        justifyContent="center"
        className={`fade-in ${isVisible ? 'visible' : ''}`}
      >
        <ExdsGrid
          columnSpan={{xl: 5}}
          justifyContent="center"
          alignItems="center"
          container
          className="validate-user-component validate-user-container"
        >
          <ExdsText primitive="p" textStyle="bodyLarge" alignment="left" className="validate-user-component validate-user-description">First we
            need to confirm your identity to ensure you have the appropriate access. To do this, we send a temporary
            access code to your mobile phone. <strong>It must be the same number we already have on file for
              you.</strong> </ExdsText>
          <ExdsSpacing margin="20px 0px 0px 0px">
            <ExdsTextInput
              labelText="Standard ID (SID)"
              microcopyText={!hasSidError ? SID_INPUT_MICROCOPY_TEXT : ''}
              errorText={hasSidError ? ERROR_ALL_FIELDS_REQUIRED : ''}
              type="text"
              value={sid || ''}
              variant="box"
              className="validate-user-component validate-user-input sid-input"
              onChange={(event)=> {
                setSid(event.target.value);
                setHasSidError(false);
              }}
              required
            />
          </ExdsSpacing>
          <div className="mobile-input-component  validate-user-component">
              <span className={['mobile-input-text',  'mobile-input-label', hasMobileNumberError ? 'mobile-input-error' : 'mobile-input-text-color'].join(' ')}>Mobile phone number</span>
              <TuxTextInput
                id="mobile-input"
                value={plainTextPhoneNumber || ''}
                required
                invalid={hasMobileNumberError}
                data-testid="phone-input"
                aria-label="Mobile phone number input"
              />
            {hasMobileNumberError && <ExdsSpacing margin={{top: '4px'}}>
              <ExdsAlert
                accessibleText="defaultVisibleText"
                fullWidth
                size="extraSmall"
                text={ERROR_MOBILE_INPUT_INVALID}
                variant="error"
              />
            </ExdsSpacing>}
            {!hasMobileNumberError && <span className="mobile-input-text mobile-input-helper-text mobile-input-text-color">{MOBILE_INPUT_MICROCOPY_TEXT}</span>}
          </div>

          <div className={hasDeliveryMethodError? 'delivery-method-selection-error validate-user-component': 'delivery-method-selection validate-user-component'}>
            <ExdsRadioGroup
              layout="horizontal"
              text="How would you like to receive your access code?"
              onChangeCommit={(selectedRadio) => handleRadioChange(selectedRadio)}
              required
            >
              <ExdsLayout
                alignContent="flex-end"
                alignItems="stretch"
                display="flex"
                flexDirection="row"
                flexWrap="nowrap"
                gap="20px"
                justifyContent="flex-start"
              >
                <div className={deliveryMethod && deliveryMethod==='SMS'? 'radio-container-select':'radio-container'}>
                  <ExdsRadio id='sms' checked={deliveryMethod === 'SMS'}>
                    <div className="radio-text">
                      <div className='text'>
                        <ExdsText primitive="p">SMS (text)
                        </ExdsText>
                        <ExdsText primitive="p">Receive access code via SMS text message</ExdsText>
                      </div>

                      <ExdsIconDevicePhone aria-hidden color="regular" size="extraLarge"/>
                    </div>
                  </ExdsRadio>
                </div>
                <div className={deliveryMethod && deliveryMethod==='VOICE'? 'radio-container-select':'radio-container'}>
                  <ExdsRadio id='voice' checked={deliveryMethod === 'VOICE'}>
                    <div className="radio-text">
                      <div className='text'>
                        <ExdsText primitive="p">Voice call</ExdsText>
                        <ExdsText primitive="p">Receive access code via an automated voice call.</ExdsText>
                      </div>
                      <ExdsIconPhoneFilled aria-hidden color="regular" size="extraLarge"/>
                    </div>
                  </ExdsRadio>
                </div>
              </ExdsLayout>
            </ExdsRadioGroup>
            {hasDeliveryMethodError && <ExdsSpacing margin={{top: '4px'}}>
              <ExdsAlert
                accessibleText="defaultVisibleText"
                fullWidth
                size="extraSmall"
                text={RADIO_BUTTONS_ERROR_TEXT}
                variant="error"
              />
            </ExdsSpacing>}
          </div>

          <ExdsSpacing margin="50px 0px 0px 0px">
            <ExdsLayout
              alignContent="flex-start"
              alignItems="stretch"
              display="flex"
              flexDirection="row"
              flexWrap="nowrap"
              gap="16px"
              justifyContent="flex-start"
              className="validate-user-component"
            >
              <ExdsButton
                color="brand"
                fullWidth
                shape="round"
                size="regular"
                statusAccessibleText="Start over button"
                text="Start over"
                variant="secondary"
                className="start-over-button"
                onClick={() => setIsStartOverModalOpen(true)}
              />
              <ExdsButton
                color="brand"
                fullWidth
                shape="round"
                size="regular"
                statusAccessibleText="submit button"
                text="Submit"
                onClick={handleSubmit}
              />
            </ExdsLayout>
          </ExdsSpacing>
        </ExdsGrid>
      </ExdsGrid>
      }
      <StartOverModal isStartOverModalOpen={isStartOverModalOpen} setIsStartOverModalOpen={setIsStartOverModalOpen}/>
    </>
  );
}
